import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  BankingBankAccountAccountResourceFragment,
  LedgerAccountFragment,
  UpsertLedgerAccountResourceDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  LedgerAccountSelector,
  formatAccountName,
} from 'modules/banking/components/LedgerAccountSelector'
import { accountNumberLabel } from 'modules/banking/lib/bankAccountNumbersHelpers'
import { formattedBankAccountNumber } from 'modules/banking/utils'
import type { BankAccountLegalEntity } from 'modules/finance/pages/Accounting/BankAccountPage'

interface Props {
  bankAccount: BankingBankAccountAccountResourceFragment
  legalEntity: BankAccountLegalEntity
  accounts: LedgerAccountFragment[]
  canManage: boolean
}
export const BankAccountRow = ({ bankAccount, legalEntity, accounts, canManage }: Props) => {
  const accountingResourceId = bankAccount.accountResource?.id
  const account = bankAccount.accountResource?.account

  const [upsertLedgerAccountResource, { loading }] = useMutation(
    UpsertLedgerAccountResourceDocument,
    {
      extractorFn: (data) => data.financeMutations.upsertFinanceLedgerAccountResource,
      update(cache) {
        cache.evict({
          id: cache.identify(bankAccount),
          fieldName: 'accountResource',
        })
      },
    }
  )

  const linkAccount = async (newAccountId: string) => {
    const result = await upsertLedgerAccountResource({
      variables: {
        id: accountingResourceId,
        legalEntityId: legalEntity.id,
        accountId: newAccountId,
        resourceId: bankAccount.id,
        resourceType: 'bankaccount',
      },
    }).then((res) => {
      track(trackingEvents.accounting.settings.bankAccounts.editLedgerAccount)
      return res
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    }
  }

  return (
    <Box padding="s0" paddingTop={'s32'}>
      <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween" gap="s16">
        <Box width="s600">
          <Text size={'400'} weight="semibold">
            {bankAccount.accountAlias}
          </Text>
          <Text size={'200'} weight="normal">
            {accountNumberLabel(bankAccount.accountNumberType) +
              ': ' +
              formattedBankAccountNumber(bankAccount)}
          </Text>
        </Box>
        <Box width="full">
          <LedgerAccountSelector
            legalEntityId={legalEntity.id}
            currentLedgerAccountName={formatAccountName(account)}
            accounts={accounts}
            canManage={canManage}
            types={['bank']}
            disabled={loading}
            onSelect={(id) => {
              linkAccount(id)
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
